import React, { useEffect, useState } from 'react'
import { Redirect, useLocation } from 'react-router'
import ContentError from 'components/ContentError'
import ContentLoading from 'components/ContentLoading'
import { isJobShortId } from 'jobs/infra/jobShortId'
import { Id } from 'types/definitions'

/**
 * Props for the component.
 */
interface Props {
  /** The `:jobId` route parameter — a canonical job ID or a short ID. */
  jobId: string | undefined
  /** Resolves a job short ID to its canonical job ID. */
  resolveShortId: (shortId: string) => Promise<Id>
  /** Builds the canonical route path for a resolved job ID. */
  buildPath: (jobId: string) => string
  children: React.ReactNode
}

type Resolution =
  | { state: 'resolving'; shortId: string | undefined }
  | { state: 'resolved'; shortId: string; canonicalJobId: string }
  | { state: 'failed'; shortId: string; error: unknown }
  | { state: 'notFound'; shortId: string }

/**
 * Lets job routes accept a job short ID in place of the canonical job ID,
 * redirecting to the canonical URL once resolved.
 *
 * Route parameters that are not short-ID-shaped — canonical IDs, typos,
 * anything else — render `children` immediately and unchanged.
 */
export default function JobShortIdRedirect({
  jobId,
  resolveShortId,
  buildPath,
  children,
}: Props) {
  const location = useLocation()
  const shortId = isJobShortId(jobId) ? jobId : undefined
  const [resolution, setResolution] = useState<Resolution>({
    state: 'resolving',
    shortId,
  })
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    if (!shortId) {
      return
    }

    let cancelled = false
    setResolution({ state: 'resolving', shortId })
    resolveShortId(shortId).then(
      (id) => {
        if (cancelled) {
          return
        }
        setResolution(
          id
            ? { state: 'resolved', shortId, canonicalJobId: id }
            : { state: 'notFound', shortId },
        )
      },
      (error) => {
        if (!cancelled) {
          setResolution({ state: 'failed', shortId, error })
        }
      },
    )

    return () => {
      cancelled = true
    }
  }, [shortId, resolveShortId, retryCount])

  if (!shortId) {
    return <>{children}</>
  }

  // The route param can change without this component remounting (the same
  // `<Route>` element re-renders it with a new `jobId`), so `resolution` may
  // still describe a previous short ID for one render pass. Rendering it
  // anyway would mount a stale `<Redirect>` before the effect below gets a
  // chance to reset the state.
  if (resolution.shortId !== shortId) {
    return <ContentLoading />
  }

  switch (resolution.state) {
    case 'resolving':
      return <ContentLoading />
    case 'resolved':
      return (
        <Redirect
          to={`${buildPath(resolution.canonicalJobId)}${location.search}${location.hash}`}
        />
      )
    case 'notFound':
      return (
        <ContentError
          title="Job not found"
          error={`No job found with ID ${resolution.shortId}.`}
        />
      )
    case 'failed':
      return (
        <ContentError
          title="Couldn't load job"
          error={resolution.error}
          onRetryClick={() => setRetryCount((count) => count + 1)}
        />
      )
    default: {
      // Turns a future `Resolution` variant that nobody handled into a
      // compile error rather than a silently blank screen.
      const exhaustive: never = resolution
      return exhaustive
    }
  }
}
